/** Matches API `normalizeBookingSlug`: lowercase segments separated by single hyphens. */
export const BOOKING_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** Path segments that must not be treated as a club booking slug. */
export const RESERVED_BOOKING_PATH_SEGMENTS = new Set(['oauth', 'assets', 'static', 'shop', 'join']);

export type DecodedClubRoute = {
  bookingSlug?: string;
  clubId?: string;
  clubNumber?: string;
};

const PREFIX = {
  slug: 's:',
  id: 'i:',
  number: 'n:',
} as const;

/** Decode internal route key (`s:…`, `i:…`, `n:…`) or legacy unprefixed values. */
export function decodeClubRouteKey(key: string | null | undefined): DecodedClubRoute {
  const raw = typeof key === 'string' ? key.trim() : '';
  if (!raw) return {};
  if (raw.startsWith(PREFIX.slug)) return { bookingSlug: raw.slice(PREFIX.slug.length).trim() };
  if (raw.startsWith(PREFIX.id)) return { clubId: raw.slice(PREFIX.id.length).trim() };
  if (raw.startsWith(PREFIX.number)) return { clubNumber: raw.slice(PREFIX.number.length).trim() };
  if (/^\d+$/.test(raw)) return { clubNumber: raw };
  return { clubId: raw };
}

export function encodeClubRouteKey(d: DecodedClubRoute): string | null {
  if (d.bookingSlug?.trim()) return `${PREFIX.slug}${d.bookingSlug.trim()}`;
  if (d.clubId?.trim()) return `${PREFIX.id}${d.clubId.trim()}`;
  if (d.clubNumber != null && String(d.clubNumber).trim() !== '') {
    return `${PREFIX.number}${String(d.clubNumber).trim()}`;
  }
  return null;
}

/** Query params for public booking APIs (priority aligns with `resolvePublicClubIdFromQuery`). */
export function publicBookingApiParamsFromClubRouteKey(
  clubRouteKey: string | null | undefined,
): URLSearchParams {
  const d = decodeClubRouteKey(clubRouteKey);
  const p = new URLSearchParams();
  if (d.bookingSlug) p.set('bookingSlug', d.bookingSlug);
  else if (d.clubId) p.set('clubId', d.clubId);
  else if (d.clubNumber) p.set('clubNumber', d.clubNumber);
  return p;
}

/** Plain object for JSON POST bodies (URLSearchParams does not spread into keys). */
export function publicBookingApiParamsObjectFromClubRouteKey(
  clubRouteKey: string | null | undefined,
): Record<string, string> {
  return Object.fromEntries(publicBookingApiParamsFromClubRouteKey(clubRouteKey).entries());
}

export function publicBookingApiParamsObjectFromWindow(): Record<string, string> {
  return Object.fromEntries(publicBookingApiParamsFromWindow().entries());
}

export function parseBookingPathClubSlug(pathname: string): string | null {
  const seg = pathname.split('/').filter(Boolean)[0];
  if (!seg || RESERVED_BOOKING_PATH_SEGMENTS.has(seg.toLowerCase())) return null;
  const lower = seg.toLowerCase();
  if (!BOOKING_SLUG_PATTERN.test(lower)) return null;
  return lower;
}

/** Strip OAuth return params so `replaceState` leaves a clean booking URL. */
export function stripOAuthReturnSearchParams(params: URLSearchParams): void {
  for (const k of ['code', 'state', 'id_token', 'session_state', 'error', 'error_description']) {
    params.delete(k);
  }
}

/** Session keys: resume partner invite after Google OAuth (`/oauth/callback/google` unmounts `/join/...`). */
export const BOOKING_PARTNER_INVITE_OAUTH_RETURN_KEY = 'booking_partner_invite_oauth_return';
export const BOOKING_PARTNER_INVITE_PENDING_CLAIM_KEY = 'booking_partner_invite_pending_claim';

export function isSafePartnerInviteReturnPath(pathname: string): boolean {
  if (typeof pathname !== 'string' || !pathname.startsWith('/join/')) return false;
  if (pathname.includes('..')) return false;
  const segment = pathname.slice('/join/'.length).split('/')[0] ?? '';
  return segment.length > 0;
}

/**
 * Resolve club identifiers from the current browser location (query first, then path slug).
 * Used outside React where there is no single memoized route key.
 */
export function publicBookingApiParamsFromWindow(): URLSearchParams {
  if (typeof window === 'undefined') return new URLSearchParams();
  const qs = new URLSearchParams(window.location.search);
  const slugParam = qs.get('bookingSlug')?.trim().toLowerCase();
  if (slugParam && BOOKING_SLUG_PATTERN.test(slugParam)) {
    return new URLSearchParams({ bookingSlug: slugParam });
  }
  const clubId = qs.get('clubId')?.trim();
  if (clubId) {
    if (/^\d+$/.test(clubId)) return new URLSearchParams({ clubNumber: clubId });
    return new URLSearchParams({ clubId });
  }
  const clubNumber = qs.get('clubNumber')?.trim();
  if (clubNumber) return new URLSearchParams({ clubNumber });
  const pathSlug = parseBookingPathClubSlug(window.location.pathname);
  if (pathSlug) return new URLSearchParams({ bookingSlug: pathSlug });
  const clubName = qs.get('clubName')?.trim();
  if (clubName) return new URLSearchParams({ clubName });
  return new URLSearchParams();
}
